import { useMutation } from '@tanstack/react-query';
import type { AuthError, Factor } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/app/AuthProvider';
import type { Json } from '@/types/database';

/**
 * Supabase auth errors expose a `code` field (e.g. `over_enrolled_factors`,
 * `mfa_phone_enroll_disabled`, `mfa_factor_name_conflict`,
 * `phone_provider_disabled`) which is far more actionable than the bare
 * `message`. We surface it in toasts and logs so admins can fix the
 * underlying cause (Twilio not configured, factor lingering, etc.) instead
 * of staring at a generic "Could not send the code".
 */
export function describeMfaError(err: unknown): string {
  if (err && typeof err === 'object' && 'message' in err) {
    const e = err as Partial<AuthError> & { code?: string; status?: number };
    const parts: string[] = [];
    if (e.message) parts.push(e.message);
    if (e.code) parts.push(`(${e.code})`);
    else if (e.status) parts.push(`(HTTP ${e.status})`);
    return parts.join(' ');
  }
  return err instanceof Error ? err.message : 'Unknown error';
}

/**
 * Thin wrappers around `supabase.auth.mfa.*` that:
 *   - normalize Supabase's slightly-different return shapes (the same
 *     enroll() call returns different fields for TOTP vs phone factors),
 *   - refresh AuthProvider state on success so the rest of the app sees
 *     the new AAL / factor list immediately,
 *   - emit one explicit audit_logs row per enroll / unenroll, since
 *     auth.mfa_factors lives in the `auth` schema and we can't put a
 *     trigger on it from public migrations.
 *
 * Phone-MFA flow:
 *   1. enrollPhone({ phoneE164 })          -> { factorId }
 *   2. challengePhone({ factorId })        -> { challengeId }   (sends SMS)
 *   3. verifyChallenge({ factorId, challengeId, code })         (OTP enrolls
 *                                                               and upgrades
 *                                                               session to
 *                                                               AAL2 in one
 *                                                               step on first
 *                                                               verify; on
 *                                                               subsequent
 *                                                               sign-ins it
 *                                                               just upgrades
 *                                                               the AAL.)
 *
 * On post-sign-in challenges (factor already verified):
 *   - challengePhone({ factorId })  -> { challengeId }   (sends SMS)
 *   - verifyChallenge({ factorId, challengeId, code })
 */

export interface EnrollPhoneInput {
  /** E.164-formatted phone number, e.g. +15551234567. */
  phoneE164: string;
  /** Optional friendly label shown in factor lists. */
  friendlyName?: string;
}

export interface VerifyChallengeInput {
  factorId: string;
  challengeId: string;
  code: string;
}

/**
 * Best-effort audit row. Failures are swallowed because losing an audit
 * write must never block the security-critical enroll/verify path —
 * Supabase's own audit feed (auth.audit_log_entries) is the source of
 * truth, this is just a convenience for the in-app Audit Log page.
 */
async function tryAuditMfa(action: string, payload: Record<string, string>) {
  try {
    const { data: userResp } = await supabase.auth.getUser();
    const userId = userResp.user?.id;
    if (!userId) return;
    const { data: profile } = await supabase
      .from('profiles')
      .select('organization_id')
      .eq('id', userId)
      .maybeSingle();
    if (!profile?.organization_id) return;
    // Cast through Json so the audit_logs.after column (jsonb) accepts the
    // record without forcing every value to be `Json`-typed at the call site.
    const after = payload as unknown as Json;
    await supabase.from('audit_logs').insert({
      organization_id: profile.organization_id,
      actor_id: userId,
      action,
      entity_type: 'mfa_factor',
      entity_id: payload.factorId ?? null,
      after,
    });
  } catch (err) {
    console.warn('[mfa] audit write failed (non-fatal)', err);
  }
}

export function useEnrollPhoneFactor() {
  const { refreshMfa } = useAuth();
  return useMutation({
    mutationFn: async ({ phoneE164, friendlyName }: EnrollPhoneInput) => {
      // Suffix the friendly name with a short timestamp so re-enrolling the
      // same phone after a stale draft can't trigger a Supabase
      // `mfa_factor_name_conflict` 422 (the factor list keeps unique names
      // per user, even across unverified drafts).
      const suffix = new Date().toISOString().slice(11, 19);
      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: 'phone',
        phone: phoneE164,
        friendlyName: `${friendlyName ?? phoneE164} (${suffix})`,
      });
      if (error) {
        console.error('[mfa] enroll failed', {
          phoneE164,
          status: error.status,
          code: (error as AuthError & { code?: string }).code,
          message: error.message,
        });
        throw error;
      }
      // For phone factors, supabase-js v2.47+ returns { id, type, friendly_name, phone }
      // and the SMS isn't sent yet — we have to call challenge() next.
      return { factorId: data.id, phone: phoneE164 };
    },
    onSuccess: async ({ factorId, phone }) => {
      // Don't audit until the factor is verified — an unverified factor
      // is just a draft. (Supabase auto-deletes unverified factors on a
      // background sweep.)
      void factorId; void phone;
      await refreshMfa();
    },
  });
}

export function useChallengePhoneFactor() {
  return useMutation({
    mutationFn: async ({ factorId }: { factorId: string }) => {
      const { data, error } = await supabase.auth.mfa.challenge({ factorId });
      if (error) {
        console.error('[mfa] challenge failed', {
          factorId,
          status: error.status,
          code: (error as AuthError & { code?: string }).code,
          message: error.message,
        });
        throw error;
      }
      return { challengeId: data.id };
    },
  });
}

export function useVerifyChallenge() {
  const { refreshMfa } = useAuth();
  return useMutation({
    mutationFn: async ({ factorId, challengeId, code }: VerifyChallengeInput) => {
      const { data, error } = await supabase.auth.mfa.verify({
        factorId,
        challengeId,
        code,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: async (_data, vars) => {
      await tryAuditMfa('mfa_verified', { factorId: vars.factorId });
      // verify() upgrades the session to AAL2 — pull fresh state so the
      // RequireMfa gate flips to "allow" immediately.
      await refreshMfa();
    },
  });
}

/**
 * Fresh server-side read of the user's factors. The AuthProvider keeps a
 * cached snapshot in `mfa.factors`, but it can lag behind reality after
 * an enroll/challenge that errored mid-flight (e.g. SMS provider 5xx left
 * a draft factor server-side that's not in our state yet). Anywhere we're
 * about to clean up "stale" factors before re-enrolling, we should use
 * this — not `useAuth().mfa.factors` — so we don't miss any.
 */
export async function listFactorsFresh(): Promise<Factor[]> {
  const { data, error } = await supabase.auth.mfa.listFactors();
  if (error) {
    console.warn('[mfa] listFactors failed', error);
    return [];
  }
  return data?.all ?? [];
}

export function useUnenrollFactor() {
  const { refreshMfa } = useAuth();
  return useMutation({
    mutationFn: async ({ factorId }: { factorId: string }) => {
      const { data, error } = await supabase.auth.mfa.unenroll({ factorId });
      if (error) throw error;
      return data;
    },
    onSuccess: async (_data, vars) => {
      await tryAuditMfa('mfa_unenrolled', { factorId: vars.factorId });
      await refreshMfa();
    },
  });
}

/**
 * Pick the factor we should challenge during sign-in. We always prefer a
 * verified phone factor (corporate policy); if multiple exist we use the
 * most recently created one, which is what the user just enrolled.
 */
export function pickPrimaryPhoneFactor(factors: Factor[]): Factor | null {
  const verifiedPhones = factors.filter(
    (f) => f.factor_type === 'phone' && f.status === 'verified',
  );
  if (verifiedPhones.length === 0) return null;
  return verifiedPhones.reduce((newest, f) =>
    new Date(f.updated_at) > new Date(newest.updated_at) ? f : newest,
  );
}
